import React, { useEffect } from "react";

import SocialProofSection from "../../shared/components/casino/SocialProofSection";
import { trackEvent } from "../../shared/services/playerEvents";
import { resumeCheckoutIntentOnce } from "../../shared/utils/checkoutIntent";

import { useGameLobby } from "./hooks/useGameLobby";
import LobbyHero from "./sections/LobbyHero";
import MobileVipBanner from "./sections/MobileVipBanner";
import SpecialOffersSection from "./sections/SpecialOffersSection";
import ProvidersSection from "./sections/ProvidersSection";
import LobbyGameRow from "./sections/LobbyGameRow";
import LiveKicker from "./sections/LiveKicker";
import PromotionsSection from "./sections/PromotionsSection";
import LatestGuidesSection from "./sections/LatestGuidesSection";
import WelcomeBonusModal from "./sections/WelcomeBonusModal";
import FirstRunActivationModal from "./components/FirstRunActivationModal";
import { useFirstRunActivation } from "./hooks/useFirstRunActivation";

/**
 * GameLobby shell.
 *
 * Intentionally thin: it composes module-scope section components and wires
 * them to the view-model returned by `useGameLobby`. All server reads are
 * react-query hooks (see ./hooks), so they run as parallel cached queries that
 * dedupe across surfaces - replacing the old ~10 serial mount-time fetches and
 * the duplicated getPurchaseStatus round-trip flagged in the 2026-06-13 audit.
 */
const GameLobby = () => {
  const {
    navigate,
    isMobile,
    isLoggedIn,

    promoList,
    promoCards,
    promosLoading,

    catalogQuery,
    gameCatalog,

    providersQuery,

    recentlyPlayedQuery,
    favoriteGamesQuery,

    specialOffersAvailable,
    purchaseStatus,

    slotsKicker,
    providersKicker,
    tableKicker,
    instantKicker,

    toggleFavorite,
    isFavorited,
    handleGameLaunch,
  } = useGameLobby();

  // First-run activation nudge (now the product default, not an A/B branch).
  // Fire-and-forget, fail-safe to control, never blocks the lobby render (see
  // hook docblock).
  const firstRunActivation = useFirstRunActivation();

  // Interrupted-checkout resume: full-page landings (Didit phone-gate return,
  // OAuth callback — including the bad_oauth_state/otp_expired error landings)
  // dump players on the lobby mid-purchase. If this mount is shortly after a
  // document load and a fresh checkout intent exists, send them back to their
  // checkout. One attempt per page load; ordinary SPA navigation to the lobby
  // is never hijacked (see resumeCheckoutIntentOnce).
  useEffect(() => {
    if (!isLoggedIn) return;
    const offerId = resumeCheckoutIntentOnce();
    if (offerId) navigate(`/checkout/${offerId}`, { replace: true });
  }, [isLoggedIn, navigate]);

  // player_events funnel: lobby view for a logged-in player. Fire-and-forget,
  // deduped in the helper. No-op for signed-out visitors (helper short-circuits
  // without a token, and we gate on isLoggedIn to avoid the wasted call).
  useEffect(() => {
    if (isLoggedIn) trackEvent("lobby_viewed");
  }, [isLoggedIn]);

  const catalogLoading = catalogQuery.isPending;
  const catalogError = catalogQuery.isError;
  const slotsPerView = isMobile ? 4 : 4;
  const clusterPerView = isMobile ? 4 : 5;

  return (
    <div className="p-3 md:p-6">
      <div className="container mx-auto px-0 md:px-4">
        {/* Hero / promo carousel (audience-gated) */}
        <LobbyHero
          isLoggedIn={isLoggedIn}
          promoList={promoList}
          promoCards={promoCards}
          promosLoading={promosLoading}
          navigate={navigate}
        />

        {/* VIP banner: mobile + logged-in only */}
        {isMobile && isLoggedIn && <MobileVipBanner navigate={navigate} />}

        {/* Social proof */}
        <SocialProofSection title="Recent Activity" className="mb-12" limit={5} />

        {/* Special offers: only when featured offers exist */}
        {specialOffersAvailable && <SpecialOffersSection navigate={navigate} />}

        {/* Providers */}
        <ProvidersSection
          providers={providersQuery.data || []}
          isLoading={providersQuery.isPending}
          isError={providersQuery.isError}
          error={providersQuery.error}
          onRetry={providersQuery.refetch}
          kicker={providersKicker}
          isMobile={isMobile}
          navigate={navigate}
        />

        {/* Slots: featured tier, fewer larger cards + more vertical breathing */}
        <div className="mb-10 md:mb-12">
          <LobbyGameRow
            title="Slots"
            kicker={slotsKicker}
            onViewAll={() => navigate("/games/slots")}
            keyPrefix="slots"
            games={gameCatalog?.slots}
            isLoading={catalogLoading}
            isError={catalogError}
            error={catalogQuery.error}
            onRetry={catalogQuery.refetch}
            limit={slotsPerView}
            skeletonCount={4}
            gridClassName="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-7"
            showFavorite={isLoggedIn}
            showAddedDate
            isFavorited={isFavorited}
            onToggleFavorite={toggleFavorite}
            onGameLaunch={handleGameLaunch}
          />
        </div>

        {/* Live / Table / Instant: tight catalog cluster */}
        <div className="space-y-6 mb-14 md:mb-16">
          <LobbyGameRow
            title="Live"
            kicker={<LiveKicker />}
            onViewAll={() => navigate("/games/live")}
            keyPrefix="live"
            games={gameCatalog?.live_casino}
            isLoading={catalogLoading}
            isError={catalogError}
            error={catalogQuery.error}
            onRetry={catalogQuery.refetch}
            limit={clusterPerView}
            showFavorite={isLoggedIn}
            isFavorited={isFavorited}
            onToggleFavorite={toggleFavorite}
            onGameLaunch={handleGameLaunch}
          />

          <LobbyGameRow
            title="Table"
            kicker={tableKicker}
            onViewAll={() => navigate("/games/table")}
            keyPrefix="table"
            games={gameCatalog?.table_games}
            isLoading={catalogLoading}
            isError={catalogError}
            error={catalogQuery.error}
            onRetry={catalogQuery.refetch}
            limit={clusterPerView}
            showFavorite={isLoggedIn}
            showNameBelow
            isFavorited={isFavorited}
            onToggleFavorite={toggleFavorite}
            onGameLaunch={handleGameLaunch}
          />

          <LobbyGameRow
            title="Instant"
            kicker={instantKicker}
            onViewAll={() => navigate("/games/instant")}
            keyPrefix="instant"
            games={gameCatalog?.instant}
            isLoading={catalogLoading}
            isError={catalogError}
            error={catalogQuery.error}
            onRetry={catalogQuery.refetch}
            limit={clusterPerView}
            showFavorite={isLoggedIn}
            showAddedDate
            isFavorited={isFavorited}
            onToggleFavorite={toggleFavorite}
            onGameLaunch={handleGameLaunch}
          />
        </div>

        {/* Promotions */}
        <PromotionsSection
          promoList={promoList}
          promoCards={promoCards}
          promosLoading={promosLoading}
          isMobile={isMobile}
          navigate={navigate}
        />

        {/* Favorites: logged-in only. Empty state shows the next action. */}
        {isLoggedIn && (
          <div className="mb-8">
            <LobbyGameRow
              title="Favorites"
              onViewAll={() => navigate("/games/favorites")}
              keyPrefix="favorite"
              games={favoriteGamesQuery.data}
              isLoading={favoriteGamesQuery.isPending}
              isError={favoriteGamesQuery.isError}
              error={favoriteGamesQuery.error}
              onRetry={favoriteGamesQuery.refetch}
              limit={clusterPerView}
              skeletonCount={clusterPerView}
              emptyTitle="No favourites yet."
              emptyHint="Tap the heart on a game and it'll show up here."
              showFavorite={isLoggedIn}
              alwaysFavorited
              onToggleFavorite={toggleFavorite}
              onGameLaunch={handleGameLaunch}
            />
          </div>
        )}

        {/* Recently played: logged-in only. */}
        {isLoggedIn && (
          <div className="mb-12">
            <LobbyGameRow
              title="Recently Played"
              onViewAll={() => navigate("/games/recent")}
              keyPrefix="recent"
              games={recentlyPlayedQuery.data}
              isLoading={recentlyPlayedQuery.isPending}
              isError={recentlyPlayedQuery.isError}
              error={recentlyPlayedQuery.error}
              onRetry={recentlyPlayedQuery.refetch}
              limit={clusterPerView}
              skeletonCount={clusterPerView}
              emptyTitle="Nothing played yet."
              emptyHint="Play a round and we'll keep track for you."
              showLastPlayed
              showFavorite={isLoggedIn}
              isFavorited={isFavorited}
              onToggleFavorite={toggleFavorite}
              onGameLaunch={handleGameLaunch}
            />
          </div>
        )}

        {/* Latest guides: blog teaser. Self-hides on empty/error/loading. */}
        <LatestGuidesSection navigate={navigate} />
      </div>

      {/* Welcome / second-purchase bonus modal */}
      <WelcomeBonusModal purchaseStatus={purchaseStatus} />

      {/* First-run activation nudge (shown to eligible first-run players only) */}
      <FirstRunActivationModal
        open={firstRunActivation.showTreatment}
        treatment={firstRunActivation.treatment}
        onDismiss={firstRunActivation.dismiss}
        isMobile={isMobile}
      />
    </div>
  );
};

export default GameLobby;
